// Phase 85.A.77 — Âm thanh "ting ting" notification cho tin nhắn mới.
//
// Web Audio API tạo tone 2 nốt nhẹ (1000Hz → 1400Hz) bằng OscillatorNode,
// không cần file mp3, ~30ms ngắn gọn không spam người dùng. Browser cấm
// autoplay nên cần user gesture đầu tiên (click/tap) để tạo AudioContext.
//
// Usage:
//   import { playTingTing, primeAudio, useNewMessageBeep } from "../lib/messageNotificationSound";
//   useNewMessageBeep(messages, { isMineKey: "isMine" });

import { useEffect, useRef } from "react";

let _ctx = null;
let _enabled = true;
let _muted = false;

// Lazy create AudioContext on first user gesture.
function _getCtx() {
  if (_ctx) return _ctx;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    _ctx = new Ctx();
    return _ctx;
  } catch {
    return null;
  }
}

/** Gọi tại click/tap đầu tiên của user để unlock AudioContext autoplay. */
export function primeAudio() {
  const ctx = _getCtx();
  if (!ctx) return;
  if (ctx.state === "suspended") {
    ctx.resume().catch(() => {});
  }
  // Local-storage user pref mute.
  try {
    _muted = window.localStorage.getItem("pickleague:msg_sound_muted") === "1";
  } catch { /* noop */ }
}

export function setMuted(muted) {
  _muted = !!muted;
  try {
    window.localStorage.setItem("pickleague:msg_sound_muted", _muted ? "1" : "0");
  } catch { /* noop */ }
}

export function isMuted() {
  try {
    return window.localStorage.getItem("pickleague:msg_sound_muted") === "1";
  } catch {
    return _muted;
  }
}

export function setEnabled(enabled) {
  _enabled = !!enabled;
}

/** Phát 2 nốt "ting ting" nhanh ~280ms total. */
export function playTingTing() {
  if (!_enabled || _muted) return;
  const ctx = _getCtx();
  if (!ctx) return;
  try {
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    const now = ctx.currentTime;
    // Nốt 1: 1000Hz
    _beep(ctx, 1000, now, 0.08);
    // Nốt 2: 1400Hz cao hơn
    _beep(ctx, 1400, now + 0.12, 0.10);
  } catch {
    // soft-fail
  }
}

function _beep(ctx, freq, startAt, duration) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.frequency.value = freq;
  osc.type = "sine";
  // Soft envelope tránh click.
  gain.gain.setValueAtTime(0, startAt);
  gain.gain.linearRampToValueAtTime(0.18, startAt + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.001, startAt + duration);
  osc.connect(gain).connect(ctx.destination);
  osc.start(startAt);
  osc.stop(startAt + duration + 0.01);
}

/**
 * Hook: phát ting ting khi messages array thêm tin nhắn mới KHÔNG phải
 * do mình gửi.
 *
 * @param {Array} messages - Array of message objects (chronological order).
 * @param {Object} opts
 *   - isIncoming(msg, prevMsgs): function trả về true nếu tin này là incoming.
 *   - enabled: bool — disable nhanh (vd page hidden).
 */
export function useNewMessageBeep(messages, opts = {}) {
  const lastIdRef = useRef(null);
  const lastCountRef = useRef(0);
  const { isIncoming, enabled = true } = opts;

  useEffect(() => {
    if (!enabled) {
      lastCountRef.current = (messages || []).length;
      lastIdRef.current = (messages || []).slice(-1)[0]?.id || null;
      return;
    }
    const arr = messages || [];
    const lastId = arr.slice(-1)[0]?.id || null;
    const prevCount = lastCountRef.current;
    const prevId = lastIdRef.current;
    // Initial load: skip first render.
    if (prevId === null && prevCount === 0) {
      lastCountRef.current = arr.length;
      lastIdRef.current = lastId;
      return;
    }
    if (arr.length > prevCount && lastId && lastId !== prevId) {
      // Has new tail msg(s). Check incoming.
      const newOnes = arr.slice(prevCount);
      const incoming = newOnes.some((m) => (isIncoming ? isIncoming(m) : false));
      if (incoming) {
        playTingTing();
      }
    }
    lastCountRef.current = arr.length;
    lastIdRef.current = lastId;
  }, [messages, isIncoming, enabled]);
}
